import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry } from '../utils/countries';
import './Dashboard.css';

function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingDomains, setPendingDomains] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [trialDomains, setTrialDomains] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserDomains, setSelectedUserDomains] = useState([]);
  const [selectedUserTrialCount, setSelectedUserTrialCount] = useState(0);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [newWorker, setNewWorker] = useState({ name: '', ipAddress: '', port: 4000 });
  const [workerApiKeyDisplay, setWorkerApiKeyDisplay] = useState(null);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [deletedAccountsPagination, setDeletedAccountsPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const { t } = useTranslation();

  // Gift state
  const [giftEmail, setGiftEmail] = useState('');
  const [giftDomain, setGiftDomain] = useState('');
  const [giftDays, setGiftDays] = useState('');
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftResult, setGiftResult] = useState(null); // { success, affected, error }

  // Email Broadcast state
  const [broadcastEmail, setBroadcastEmail] = useState('');
  const [broadcastSubjectTr, setBroadcastSubjectTr] = useState('');
  const [broadcastSubjectEn, setBroadcastSubjectEn] = useState('');
  const [broadcastContentTr, setBroadcastContentTr] = useState('');
  const [broadcastContentEn, setBroadcastContentEn] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null); // { success, sent, skipped, error }


  const handleGift = async () => {
    if (!giftEmail.trim() || !giftDays || parseInt(giftDays) <= 0) return;
    setGiftLoading(true);
    setGiftResult(null);
    try {
      const res = await axios.post('/admin/gift-days', {
        email: giftEmail.trim(),
        domain_url: giftDomain.trim() || null,
        days: parseInt(giftDays)
      });
      setGiftResult({ success: true, affected: res.data.affected, days: res.data.days });
      setGiftEmail('');
      setGiftDomain('');
      setGiftDays('');
    } catch (err) {
      const errorCode = err.response?.data?.error_code || 'NETWORK_ERROR';
      setGiftResult({ success: false, error: errorCode });
    }
    setGiftLoading(false);
  };

  const handleBroadcast = async () => {
    if (!broadcastEmail.trim()) return;
    if (!broadcastSubjectTr.trim() && !broadcastSubjectEn.trim()) return;
    if (!broadcastContentTr.trim() && !broadcastContentEn.trim()) return;
    setBroadcastLoading(true);
    setBroadcastResult(null);
    try {
      const res = await axios.post('/admin/send-email', {
        email: broadcastEmail.trim(),
        subjectTr: broadcastSubjectTr.trim(),
        subjectEn: broadcastSubjectEn.trim(),
        contentTr: broadcastContentTr.trim(),
        contentEn: broadcastContentEn.trim()
      });
      setBroadcastResult({ success: true, sent: res.data.sent, skipped: res.data.skipped });
      setBroadcastEmail('');
      setBroadcastSubjectTr('');
      setBroadcastSubjectEn('');
      setBroadcastContentTr('');
      setBroadcastContentEn('');
    } catch (err) {
      const errorCode = err.response?.data?.error_code || 'NETWORK_ERROR';
      setBroadcastResult({ success: false, error: errorCode });
    }
    setBroadcastLoading(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await axios.get('/admin/stats');
      setStats(statsRes.data);

      if (activeTab === 'users') {
        const [usersRes, pendingRes] = await Promise.all([
          axios.get('/admin/users'),
          axios.get('/admin/users/pending')
        ]);
        setAllUsers(usersRes.data.users);
        setPendingUsers(pendingRes.data.users);
      }
      if (activeTab === 'domains') {
        const [domainsRes, pendingRes] = await Promise.all([
          axios.get('/admin/domains'),
          axios.get('/admin/domains/pending')
        ]);
        setAllDomains(domainsRes.data.domains);
        setPendingDomains(pendingRes.data.domains);
      }
      if (activeTab === 'trial-domains') {
        const trialRes = await axios.get('/admin/trial-domains');
        setTrialDomains(trialRes.data.trialDomains);
      }
      if (activeTab === 'subscriptions') {
        const subRes = await axios.get('/admin/subscriptions');
        setSubscriptions(subRes.data.subscriptions);
      }
      if (activeTab === 'pricing') {
        const pricingRes = await axios.get('/admin/pricing');
        setPricingSettings(pricingRes.data.pricing);
      }
      if (activeTab === 'workers') {
        const workersRes = await axios.get('/admin/screenshot-workers');
        setWorkers(workersRes.data.workers);
      }
      if (activeTab === 'deleted-accounts') {
        const p = deletedAccountsPagination.page;
        const l = deletedAccountsPagination.limit;
        const res = await axios.get(`/admin/deleted-accounts?page=${p}&limit=${l}`);
        setDeletedAccounts(res.data.deletedAccounts);
        setDeletedAccountsPagination(prev => ({
          ...prev,
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
          hasNext: res.data.pagination.hasNext,
          hasPrev: res.data.pagination.hasPrev
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, deletedAccountsPagination.page, deletedAccountsPagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ═══════════════════════════════════════════════════════════════
  // DELETED ACCOUNTS
  // ═══════════════════════════════════════════════════════════════
  const handleDeletedAccountsPageChange = (newPage) => {
    setDeletedAccountsPagination(prev => ({ ...prev, page: newPage }));
  };

  // ═══════════════════════════════════════════════════════════════
  // USER DETAIL
  // ═══════════════════════════════════════════════════════════════
  const handleUserClick = async (userId) => {
    setUserDetailLoading(true);
    try {
      const res = await axios.get(`/admin/users/${userId}`);
      setSelectedUser(res.data.user);
      setSelectedUserDomains(res.data.domains);
      setSelectedUserTrialCount(res.data.trialUsageCount);
    } catch (error) {
      alert('Veri alınamadı');
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleCloseUserDetail = () => {
    setSelectedUser(null);
    setSelectedUserDomains([]);
    setSelectedUserTrialCount(0);
  };

  // ═══════════════════════════════════════════════════════════════
  // USER ACTIONS
  // ═══════════════════════════════════════════════════════════════
  const handleUserActivation = async (userId, isActive) => {
    try {
      await axios.patch(`/admin/users/${userId}/activate`, { isActive });
      fetchData();
      if (selectedUser && selectedUser.id === userId) {
        handleUserClick(userId);
      }
    } catch (error) {
      alert('Güncelleme başarısız');
    }
  };

  const handleUserDelete = async (userId, email) => {
    if (!window.confirm(`"${email}" kullanıcısını ve tüm verilerini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }
    try {
      await axios.delete(`/admin/users/${userId}`);
      fetchData();
      handleCloseUserDetail();
    } catch (error) {
      const msg = error.response?.data?.message || 'Silme başarısız';
      alert(msg);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DOMAIN ACTIONS
  // ═══════════════════════════════════════════════════════════════
  const handleDomainApproval = async (domainId, status, isMonitoring = false) => {
    try {
      await axios.patch(`/admin/domains/${domainId}/approve`, { status, isMonitoring });
      fetchData();
    } catch (error) {
      alert('Güncelleme başarısız');
    }
  };

  const handleDomainDelete = async (domainId) => {
    if (window.confirm(t('confirm.deleteDomain') || 'Bu domaini silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`/admin/domains/${domainId}`);
        fetchData();
      } catch (error) {
        alert('Silme başarısız');
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // TRIAL DOMAIN ACTIONS
  // ═══════════════════════════════════════════════════════════════
  const handleTrialDelete = async (id) => {
    if (window.confirm('Bu trial kaydını silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`/admin/trial-domains/${id}`);
        fetchData();
      } catch (error) {
        alert('Silme başarısız');
      }
    }
  };

  const handleTrialDeleteWithDomain = async (id) => {
    if (window.confirm('Bu trial kaydını VE ilgili domaini tamamen silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!')) {
      try {
        await axios.delete(`/admin/trial-domains/${id}/with-domain`);
        fetchData();
      } catch (error) {
        alert('Silme başarısız');
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PRICING SETTINGS ACTIONS
  // ═══════════════════════════════════════════════════════════════
  const handlePricingEdit = () => {
    setEditingPricing({ ...pricingSettings });
  };

  // ═══════════════════════════════════════════════════════════════
  // SCREENSHOT WORKER ACTIONS
  // ═══════════════════════════════════════════════════════════════
  const handleAddWorker = async () => {
    if (!newWorker.name || !newWorker.ipAddress) {
      alert('İsim ve IP adresi zorunlu');
      return;
    }
    try {
      const res = await axios.post('/admin/screenshot-workers', {
        name: newWorker.name,
        ipAddress: newWorker.ipAddress,
        port: newWorker.port || 4000
      });
      setWorkerApiKeyDisplay(res.data.worker.api_key);
      setNewWorker({ name: '', ipAddress: '', port: 4000 });
      setShowAddWorker(false);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.error_code === 'WORKER_IP_EXISTS'
        ? 'Bu IP adresi zaten kayıtlı'
        : error.response?.data?.message || 'Worker eklenemedi';
      alert(msg);
    }
  };

  const handleWorkerToggle = async (workerId, currentState) => {
    try {
      await axios.patch(`/admin/screenshot-workers/${workerId}`, { isActive: !currentState });
      fetchData();
    } catch (error) {
      alert('Güncelleme başarısız');
    }
  };

  const handleWorkerDelete = async (workerId, name) => {
    if (!window.confirm(`"${name}" worker'ını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`/admin/screenshot-workers/${workerId}`);
      fetchData();
    } catch (error) {
      alert('Silme başarısız');
    }
  };

  const handleWorkerHealthCheck = async (workerId) => {
    try {
      const res = await axios.post(`/admin/screenshot-workers/${workerId}/health-check`);
      const result = res.data;
      alert(result.healthy
        ? `✅ Worker sağlıklı (${result.responseTime}ms)`
        : `❌ Worker yanıt vermiyor: ${result.error || 'Bilinmeyen hata'}`
      );
      fetchData();
    } catch (error) {
      alert('Health check başarısız');
    }
  };

  const handleRegenerateKey = async (workerId, name) => {
    if (!window.confirm(`"${name}" için API key yenilenecek. Worker sunucusundaki .env dosyasını da güncellemeniz gerekecek. Devam?`)) return;
    try {
      const res = await axios.post(`/admin/screenshot-workers/${workerId}/regenerate-key`);
      setWorkerApiKeyDisplay(res.data.worker.api_key);
      fetchData();
    } catch (error) {
      alert('Key yenileme başarısız');
    }
  };

  const handlePricingSave = async () => {
    try {
      await axios.patch('/admin/pricing', {
        monthlyPriceUsd: Number(editingPricing.monthly_price_usd),
        currencyMultiplierTry: Number(editingPricing.currency_multiplier_try),
        minMonths: Number(editingPricing.min_months),
        maxMonths: Number(editingPricing.max_months),
        freeTrialDays: Number(editingPricing.free_trial_days)
      });
      setEditingPricing(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.message || 'Güncelleme başarısız';
      alert(msg);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════
  const formatDuration = (months) => {
    const m = parseInt(months);
    if (isNaN(m) || m === 0) return '1 Hafta (Trial)';
    if (m === 1) return '1 Ay';
    return `${m} Ay`;
  };

  const formatPrice = (price) => {
    const p = parseFloat(price);
    if (isNaN(p) || p === 0) return 'Ücretsiz';
    return `$${p.toFixed(2)}`;
  };

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  // ═══════════════════════════════════════════════════════════════
  // TAB CONFIG
  // ═══════════════════════════════════════════════════════════════
  const adminTabs = [
    { key: 'overview', label: t('tab.overview') || 'Genel Bakış', icon: '📊' },
    { key: 'users', label: t('tab.users') || 'Kullanıcılar', icon: '👥' },
    { key: 'domains', label: t('tab.domains') || 'Domainler', icon: '🌐' },
    { key: 'trial-domains', label: 'Trial Domainler', icon: '🎁' },
    { key: 'subscriptions', label: 'Abonelikler', icon: '💳' },
    { key: 'pricing', label: 'Fiyatlandırma', icon: '💰' },
    { key: 'workers', label: 'Screenshot Workers', icon: '🖥️' },
    { key: 'deleted-accounts', label: 'Silinen Hesaplar', icon: '🗑️' },
    { key: 'gift', label: 'Hediye Gün', icon: '🎁' },
    { key: 'email-broadcast', label: 'Mail Gönder', icon: '📧' },
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <h1>{t('admin.title')}</h1>
          <p>{t('admin.description')}</p>
        </header>

        {/* Admin Tabs */}
        <div className="tabs">
          {adminTabs.map(tab => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => {
                setActiveTab(tab.key);
                setSelectedUser(null);
              }}
            >
              <span style={{ marginRight: '6px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading">{t('loading.text')}</div>
        ) : (
          <>
            {/* ═══════════════════ OVERVIEW ═══════════════════ */}
            {activeTab === 'overview' && stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>{t('stats.totalUsers')}</h3>
                  <p className="stat-number">{stats.total_users}</p>
                </div>
                <div className="stat-card warning">
                  <h3>{t('stats.pendingUsers')}</h3>
                  <p className="stat-number">{stats.pending_users}</p>
                </div>
                <div className="stat-card">
                  <h3>{t('stats.totalDomains')}</h3>
                  <p className="stat-number">{stats.total_domains}</p>
                </div>
                <div className="stat-card warning">
                  <h3>{t('stats.pendingDomains')}</h3>
                  <p className="stat-number">{stats.pending_domains}</p>
                </div>
                <div className="stat-card success">
                  <h3>{t('stats.activeMonitors')}</h3>
                  <p className="stat-number">{stats.active_monitors}</p>
                </div>
                <div className="stat-card info">
                  <h3>{t('stats.checks24h')}</h3>
                  <p className="stat-number">{stats.checks_24h}</p>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                  <h3>Toplam Trial</h3>
                  <p className="stat-number">{stats.total_trials}</p>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                  <h3>Aktif Abonelikler</h3>
                  <p className="stat-number">{stats.active_subscriptions}</p>
                </div>
              </div>
            )}

            {/* ═══════════════════ USERS ═══════════════════ */}
            {activeTab === 'users' && (
              <>
                {/* User Detail Modal */}
                {selectedUser && (
                  <div className="admin-modal-overlay" onClick={handleCloseUserDetail}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="admin-modal-header">
                        <h2>👤 {selectedUser.full_name || selectedUser.email}</h2>
                        <button className="btn-close" onClick={handleCloseUserDetail}>✕</button>
                      </div>

                      {userDetailLoading ? (
                        <div className="loading">{t('loading.text')}</div>
                      ) : (
                        <div className="admin-modal-body">
                          {/* User Info */}
                          <div className="user-detail-info">
                            <div className="detail-row">
                              <span className="detail-label">Email:</span>
                              <span>{selectedUser.email}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Ad Soyad:</span>
                              <span>{selectedUser.full_name || '-'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Rol:</span>
                              <span className={`badge ${selectedUser.role}`}>
                                {selectedUser.role === 'admin' ? t('role.admin') : t('role.user')}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Durum:</span>
                              <span className={`badge ${selectedUser.is_active ? 'success' : 'warning'}`}>
                                {selectedUser.is_active ? 'Aktif' : 'Pasif'}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Ülke / Dil:</span>
                              <span>{selectedUser.country || '-'} / {selectedUser.language || '-'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Kayıt Tarihi:</span>
                              <span>{formatDateForCountry(selectedUser.created_at, selectedUser?.country)}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Trial Kullanımı:</span>
                              <span>{selectedUserTrialCount} / 2</span>
                            </div>
                          </div>

                          {/* User Actions */}
                          {selectedUser.role !== 'admin' && (
                            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                className={`btn-small ${selectedUser.is_active ? 'btn-warning' : 'btn-success'}`}
                                onClick={() => handleUserActivation(selectedUser.id, !selectedUser.is_active)}
                              >
                                {selectedUser.is_active ? '⏸ Pasife Al' : '✅ Aktif Et'}
                              </button>
                              <button
                                className="btn-small btn-danger"
                                onClick={() => handleUserDelete(selectedUser.id, selectedUser.email)}
                              >
                                🗑 Kullanıcıyı Sil
                              </button>
                            </div>
                          )}

                          {/* User's Domains */}
                          <h3 style={{ marginTop: '24px', marginBottom: '10px' }}>
                            🌐 Domainler ({selectedUserDomains.length})
                          </h3>
                          {selectedUserDomains.length === 0 ? (
                            <p className="empty-message">Bu kullanıcının henüz domaini yok.</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Domain</th>
                                    <th>Durum</th>
                                    <th>Paket</th>
                                    <th>Başlangıç</th>
                                    <th>Bitiş</th>
                                    <th>Ödeme</th>
                                    <th>Monitoring</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedUserDomains.map(domain => {
                                    const durMonths = domain.duration_months_raw != null ? Math.floor(parseFloat(domain.duration_months_raw)) : null;
                                    const planPrice = domain.plan_price != null ? parseFloat(domain.plan_price) : null;
                                    const expired = isExpired(domain.subscription_end_date_sub);
                                    return (
                                      <tr key={domain.id} style={expired ? { opacity: 0.6 } : {}}>
                                        <td>
                                          <a href={`http://${domain.domain_url}`} target="_blank" rel="noopener noreferrer">
                                            {domain.domain_url}
                                          </a>
                                        </td>
                                        <td>
                                          <span className={`badge ${domain.status}`}>
                                            {domain.status === 'approved' ? 'Onaylı' :
                                             domain.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}
                                          </span>
                                        </td>
                                        <td>
                                          {domain.plan_name ? (
                                            <>
                                              {domain.plan_name}
                                              <br />
                                              <small style={{ color: '#6b7280' }}>
                                                {formatDuration(durMonths)} - {formatPrice(planPrice)}
                                              </small>
                                            </>
                                          ) : (
                                            <span style={{ color: '#9ca3af' }}>-</span>
                                          )}
                                        </td>
                                        <td>
                                          {domain.subscription_start_date
                                            ? formatDateForCountry(domain.subscription_start_date, user?.country)
                                            : '-'}
                                        </td>
                                        <td>
                                          <span style={expired ? { color: '#ef4444', fontWeight: 600 } : {}}>
                                            {domain.subscription_end_date_sub
                                              ? formatDateForCountry(domain.subscription_end_date_sub, user?.country)
                                              : '-'}
                                          </span>
                                          {expired && <small style={{ color: '#ef4444', display: 'block' }}>Süresi dolmuş</small>}
                                        </td>
                                        <td>
                                          {domain.payment_status ? (
                                            <span className={`badge ${domain.payment_status === 'completed' ? 'success' : 'warning'}`}>
                                              {domain.payment_status === 'completed' ? 'Ödendi' :
                                               domain.payment_status === 'pending' ? 'Beklemede' :
                                               domain.payment_status === 'failed' ? 'Başarısız' : domain.payment_status}
                                            </span>
                                          ) : '-'}
                                        </td>
                                        <td>
                                          <span className={`badge ${domain.is_monitoring ? 'success' : 'secondary'}`}>
                                            {domain.is_monitoring ? 'Aktif' : 'Pasif'}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pending Users */}
                {pendingUsers.length > 0 && (
                  <section className="section">
                    <h2>{t('users.pendingTitle')} ({pendingUsers.length})</h2>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('user.email')}</th>
                          <th>{t('user.fullName')}</th>
                          <th>{t('user.registeredAt')}</th>
                          <th>{t('user.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingUsers.map(u => (
                          <tr key={u.id}>
                            <td>{u.email}</td>
                            <td>{u.full_name || '-'}</td>
                            <td>{formatDateForCountry(u.created_at, u?.country)}</td>
                            <td>
                              <button className="btn-small btn-success" onClick={() => handleUserActivation(u.id, true)}>
                                {t('action.approve')}
                              </button>
                              <button className="btn-small btn-danger" onClick={() => handleUserActivation(u.id, false)} style={{ marginLeft: '4px' }}>
                                {t('action.reject')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                )}

                {/* All Users */}
                <section className="section">
                  <h2>{t('tab.users')} ({allUsers.length})</h2>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('user.email')}</th>
                        <th>{t('user.fullName')}</th>
                        <th>{t('user.role')}</th>
                        <th>{t('user.status')}</th>
                        <th>Domain</th>
                        <th>Trial</th>
                        <th>{t('user.registeredAt')}</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(u => (
                        <tr key={u.id} className="clickable-row">
                          <td
                            onClick={() => handleUserClick(u.id)}
                            style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 500 }}
                          >
                            {u.email}
                          </td>
                          <td onClick={() => handleUserClick(u.id)} style={{ cursor: 'pointer' }}>
                            {u.full_name || '-'}
                          </td>
                          <td>
                            <span className={`badge ${u.role}`}>
                              {u.role === 'admin' ? t('role.admin') : t('role.user')}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${u.is_active ? 'success' : 'warning'}`}>
                              {u.is_active ? t('filter.active') : t('filter.passive')}
                            </span>
                          </td>
                          <td>{u.domain_count || 0}</td>
                          <td>{u.trial_count || 0}/2</td>
                          <td>{formatDateForCountry(u.created_at, u?.country)}</td>
                          <td>
                            {u.role !== 'admin' && (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                                <button
                                  className={`btn-small ${u.is_active ? 'btn-warning' : 'btn-success'}`}
                                  onClick={(e) => { e.stopPropagation(); handleUserActivation(u.id, !u.is_active); }}
                                  title={u.is_active ? 'Pasife Al' : 'Aktif Et'}
                                >
                                  {u.is_active ? '⏸' : '✅'}
                                </button>
                                <button
                                  className="btn-small btn-danger"
                                  onClick={(e) => { e.stopPropagation(); handleUserDelete(u.id, u.email); }}
                                  title="Sil"
                                >
                                  🗑
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </>
            )}

            {/* ═══════════════════ DOMAINS ═══════════════════ */}
            {activeTab === 'domains' && (
              <>
                <section className="section">
                  <h2>{t('domains.pendingTitle')} ({pendingDomains.length})</h2>
                  {pendingDomains.length === 0 ? (
                    <p className="empty-message">{t('domains.noPending')}</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('domain.url')}</th>
                          <th>{t('user.email')}</th>
                          <th>{t('domain.addedAt')}</th>
                          <th>{t('user.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingDomains.map(domain => (
                          <tr key={domain.id}>
                            <td>
                              <a href={`http://${domain.domain_url}`} target="_blank" rel="noopener noreferrer">
                                {domain.domain_url}
                              </a>
                            </td>
                            <td>{domain.email}</td>
                            <td>{formatDateForCountry(domain.created_at, user?.country)}</td>
                            <td>
                              <button
                                className="btn-small btn-success"
                                onClick={() => handleDomainApproval(domain.id, 'approved', true)}
                              >
                                {t('action.approve')} & {t('monitoring')}
                              </button>
                              <button
                                className="btn-small btn-danger"
                                onClick={() => handleDomainApproval(domain.id, 'rejected')}
                                style={{ marginLeft: '4px' }}
                              >
                                {t('action.reject')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>

                <section className="section">
                  <h2>{t('tab.domains')} ({allDomains.length})</h2>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('domain.url')}</th>
                        <th>{t('user.email')}</th>
                        <th>{t('domain.status')}</th>
                        <th>{t('domain.monitoring')}</th>
                        <th>{t('domain.addedAt')}</th>
                        <th>{t('user.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDomains.map(domain => (
                        <tr key={domain.id}>
                          <td>
                            <a href={`http://${domain.domain_url}`} target="_blank" rel="noopener noreferrer">
                              {domain.domain_url}
                            </a>
                          </td>
                          <td>{domain.email}</td>
                          <td>
                            <span className={`badge ${domain.status}`}>
                              {domain.status === 'approved'
                                ? t('status.approved')
                                : domain.status === 'rejected'
                                ? t('status.rejected')
                                : t('status.pending')}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${domain.is_monitoring ? 'success' : 'secondary'}`}>
                              {domain.is_monitoring ? t('filter.active') : t('filter.passive')}
                            </span>
                          </td>
                          <td>{formatDateForCountry(domain.created_at, user?.country)}</td>
                          <td>
                            <button
                              className="btn-small btn-danger"
                              onClick={() => handleDomainDelete(domain.id)}
                            >
                              {t('action.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </>
            )}

            {/* ═══════════════════ TRIAL DOMAINS ═══════════════════ */}
            {activeTab === 'trial-domains' && (
              <section className="section">
                <h2>🎁 Trial Domainler ({trialDomains.length})</h2>
                <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '0.9rem' }}>
                  Ücretsiz deneme kullanan tüm domainler (aktif + süresi dolmuş)
                </p>
                {trialDomains.length === 0 ? (
                  <p className="empty-message">Henüz trial kullanımı bulunmuyor.</p>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Kullanıcı</th>
                        <th>Paket</th>
                        <th>Trial Başlangıç</th>
                        <th>Abo. Bitiş</th>
                        <th>Durum</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trialDomains.map(td => {
                        const expired = isExpired(td.subscription_end);
                        const subActive = td.subscription_is_active && !expired;
                        return (
                          <tr key={td.id} style={expired ? { opacity: 0.65 } : {}}>
                            <td>
                              {td.domain_url ? (
                                <a href={`http://${td.domain_url}`} target="_blank" rel="noopener noreferrer">
                                  {td.domain_url}
                                </a>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>{td.trial_domain_url}</span>
                              )}
                            </td>
                            <td>
                              {td.email || '-'}
                              {td.full_name && <br />}
                              {td.full_name && <small style={{ color: '#6b7280' }}>{td.full_name}</small>}
                            </td>
                            <td>
                              {td.plan_name ? (
                                <>
                                  {td.plan_name}
                                  <br />
                                  <small style={{ color: '#6b7280' }}>
                                    {formatPrice(td.plan_price)}
                                  </small>
                                </>
                              ) : '-'}
                            </td>
                            <td>{td.trial_started_at ? formatDateForCountry(td.trial_started_at, user?.country) : '-'}</td>
                            <td>
                              <span style={expired ? { color: '#ef4444', fontWeight: 600 } : {}}>
                                {td.subscription_end ? formatDateForCountry(td.subscription_end, user?.country) : '-'}
                              </span>
                              {expired && <small style={{ color: '#ef4444', display: 'block' }}>Süresi dolmuş</small>}
                            </td>
                            <td>
                              <span className={`badge ${subActive ? 'success' : expired ? 'danger' : 'secondary'}`}>
                                {subActive ? 'Aktif' : expired ? 'Süresi Dolmuş' : 'Pasif'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                <button
                                  className="btn-small btn-danger"
                                  onClick={() => handleTrialDelete(td.id)}
                                  title="Sadece trial kaydını sil"
                                >
                                  Trial Sil
                                </button>
                                {td.domain_id && (
                                  <button
                                    className="btn-small"
                                    onClick={() => handleTrialDeleteWithDomain(td.id)}
                                    title="Trial + Domain'i tamamen sil"
                                    style={{ backgroundColor: '#7c3aed', color: 'white', border: 'none' }}
                                  >
                                    Tümünü Sil
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {/* ═══════════════════ SUBSCRIPTIONS ═══════════════════ */}
            {activeTab === 'subscriptions' && (
              <section className="section">
                <h2>💳 Tüm Abonelikler ({subscriptions.length})</h2>
                <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '0.9rem' }}>
                  Ücretli ve ücretsiz tüm domain abonelikleri (trial dahil)
                </p>
                {subscriptions.length === 0 ? (
                  <p className="empty-message">Henüz abonelik kaydı bulunmuyor.</p>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Kullanıcı</th>
                        <th>Paket</th>
                        <th>Başlangıç</th>
                        <th>Bitiş</th>
                        <th>Durum</th>
                        <th>Ödeme</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map(sub => {
                        const expired = isExpired(sub.end_date);
                        return (
                          <tr key={sub.id} style={(!sub.is_active || expired) ? { opacity: 0.6 } : {}}>
                            <td>
                              <a href={`http://${sub.domain_url}`} target="_blank" rel="noopener noreferrer">
                                {sub.domain_url}
                              </a>
                            </td>
                            <td>
                              {sub.email}
                              {sub.full_name && <br />}
                              {sub.full_name && <small style={{ color: '#6b7280' }}>{sub.full_name}</small>}
                            </td>
                            <td>
                              {sub.plan_name ? (
                                <>
                                  {sub.plan_name}
                                  <br />
                                  <small style={{ color: '#6b7280' }}>
                                    {formatDuration(sub.duration_months)} - {formatPrice(sub.plan_price)}
                                  </small>
                                </>
                              ) : '-'}
                            </td>
                            <td>{formatDateForCountry(sub.start_date, user?.country)}</td>
                            <td>
                              <span style={expired ? { color: '#ef4444', fontWeight: 600 } : {}}>
                                {formatDateForCountry(sub.end_date, user?.country)}
                              </span>
                              {expired && <small style={{ color: '#ef4444', display: 'block' }}>Süresi dolmuş</small>}
                            </td>
                            <td>
                              <span className={`badge ${sub.is_active && !expired ? 'success' : 'secondary'}`}>
                                {sub.is_active && !expired ? 'Aktif' : 'Pasif'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${
                                sub.payment_status === 'completed' ? 'success' :
                                sub.payment_status === 'pending' ? 'warning' : 'danger'
                              }`}>
                                {sub.payment_status === 'completed' ? 'Ödendi' :
                                 sub.payment_status === 'pending' ? 'Beklemede' :
                                 sub.payment_status === 'failed' ? 'Başarısız' :
                                 sub.payment_status === 'refunded' ? 'İade' : sub.payment_status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {/* ═══════════════════ PRICING SETTINGS ═══════════════════ */}
            {activeTab === 'pricing' && (
              <section className="section">
                <h2>💰 Fiyatlandırma Ayarları</h2>
                <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.9rem' }}>
                  Aylık USD fiyat ve TL kur çarpanı ayarları. Kullanıcılar ödeme formunda ay seçimi yaparak toplam tutarı görür.
                </p>

                {pricingSettings ? (
                  editingPricing ? (
                    // DÜZENLEME MODU
                    <div className="pricing-card">
                      <div className="pricing-grid">
                        <div className="pricing-field">
                          <label>Aylık Fiyat (USD)</label>
                          <div className="pricing-input-group">
                            <span className="pricing-prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingPricing.monthly_price_usd}
                              onChange={(e) => setEditingPricing({ ...editingPricing, monthly_price_usd: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="pricing-field">
                          <label>TL Kur Çarpanı</label>
                          <div className="pricing-input-group">
                            <span className="pricing-prefix">₺</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingPricing.currency_multiplier_try}
                              onChange={(e) => setEditingPricing({ ...editingPricing, currency_multiplier_try: e.target.value })}
                            />
                          </div>
                          <small style={{ color: '#6b7280' }}>1 USD = {editingPricing.currency_multiplier_try} TL</small>
                        </div>

                        <div className="pricing-field">
                          <label>Minimum Ay</label>
                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={editingPricing.min_months}
                            onChange={(e) => setEditingPricing({ ...editingPricing, min_months: e.target.value })}
                          />
                        </div>

                        <div className="pricing-field">
                          <label>Maximum Ay</label>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={editingPricing.max_months}
                            onChange={(e) => setEditingPricing({ ...editingPricing, max_months: e.target.value })}
                          />
                        </div>

                        <div className="pricing-field">
                          <label>Ücretsiz Deneme (Gün)</label>
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={editingPricing.free_trial_days}
                            onChange={(e) => setEditingPricing({ ...editingPricing, free_trial_days: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* Fiyat Önizleme */}
                      <div className="pricing-preview">
                        <h4>📋 Fiyat Önizleme</h4>
                        <table className="data-table" style={{ marginTop: '8px' }}>
                          <thead>
                            <tr>
                              <th>Süre</th>
                              <th>USD</th>
                              <th>TRY</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1, 2, 3, 6, 12].map(m => {
                              const usd = (parseFloat(editingPricing.monthly_price_usd) || 0) * m;
                              const tryAmount = usd * (parseFloat(editingPricing.currency_multiplier_try) || 0);
                              return (
                                <tr key={m}>
                                  <td>{m} Ay</td>
                                  <td>${usd.toFixed(2)}</td>
                                  <td>₺{tryAmount.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                        <button className="btn-small btn-success" onClick={handlePricingSave}>💾 Kaydet</button>
                        <button className="btn-small btn-secondary" onClick={() => setEditingPricing(null)}>İptal</button>
                      </div>
                    </div>
                  ) : (
                    // GÖRÜNTÜLEME MODU
                    <div className="pricing-card">
                      <div className="pricing-grid">
                        <div className="pricing-display">
                          <span className="pricing-label">Aylık Fiyat</span>
                          <span className="pricing-value">${pricingSettings.monthly_price_usd}</span>
                        </div>
                        <div className="pricing-display">
                          <span className="pricing-label">TL Kur Çarpanı</span>
                          <span className="pricing-value">₺{pricingSettings.currency_multiplier_try}</span>
                          <small style={{ color: '#6b7280' }}>1 USD = {pricingSettings.currency_multiplier_try} TL</small>
                        </div>
                        <div className="pricing-display">
                          <span className="pricing-label">Aylık TL Fiyat</span>
                          <span className="pricing-value pricing-highlight">
                            ₺{(pricingSettings.monthly_price_usd * pricingSettings.currency_multiplier_try).toFixed(2)}
                          </span>
                        </div>
                        <div className="pricing-display">
                          <span className="pricing-label">Ay Aralığı</span>
                          <span className="pricing-value">{pricingSettings.min_months} - {pricingSettings.max_months} ay</span>
                        </div>
                        <div className="pricing-display">
                          <span className="pricing-label">Ücretsiz Deneme</span>
                          <span className="pricing-value">{pricingSettings.free_trial_days} gün</span>
                        </div>
                      </div>

                      {/* Fiyat Tablosu */}
                      <div className="pricing-preview">
                        <h4>📋 Müşteriye Görünen Fiyatlar</h4>
                        <table className="data-table" style={{ marginTop: '8px' }}>
                          <thead>
                            <tr>
                              <th>Süre</th>
                              <th>USD</th>
                              <th>TRY</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(
                              { length: pricingSettings.max_months - pricingSettings.min_months + 1 },
                              (_, i) => pricingSettings.min_months + i
                            ).map(m => {
                              const usd = pricingSettings.monthly_price_usd * m;
                              const tryAmount = usd * pricingSettings.currency_multiplier_try;
                              return (
                                <tr key={m}>
                                  <td><strong>{m} Ay</strong></td>
                                  <td>${usd.toFixed(2)}</td>
                                  <td>₺{tryAmount.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {pricingSettings.updated_at && (
                        <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '12px' }}>
                          Son güncelleme: {formatDateForCountry(pricingSettings.updated_at, user?.country)}
                        </p>
                      )}

                      <div style={{ marginTop: '16px' }}>
                        <button className="btn-small btn-primary" onClick={handlePricingEdit}>✏️ Düzenle</button>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="empty-message">Fiyatlandırma ayarları yükleniyor...</p>
                )}
              </section>
            )}

            {/* ═══════════════════ SCREENSHOT WORKERS ═══════════════════ */}
            {activeTab === 'workers' && (
              <section className="section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2>🖥️ Screenshot Worker Sunucuları ({workers.length})</h2>
                  <button className="btn-small btn-primary" onClick={() => setShowAddWorker(true)}>+ Yeni Worker Ekle</button>
                </div>

                {/* API Key Display */}
                {workerApiKeyDisplay && (
                  <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                    <strong>⚠️ API Key (bu sadece bir kez gösterilir!):</strong>
                    <div style={{ marginTop: '8px', background: '#fff', padding: '12px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', userSelect: 'all' }}>
                      {workerApiKeyDisplay}
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '8px' }}>
                      Bu key'i worker sunucunun .env dosyasına API_KEY olarak ekleyin. Kaybederseniz "Key Yenile" ile yeni key oluşturabilirsiniz.
                    </p>
                    <button className="btn-small btn-secondary" onClick={() => setWorkerApiKeyDisplay(null)} style={{ marginTop: '8px' }}>Tamam, Kopyaladım</button>
                  </div>
                )}

                {/* Add Worker Form */}
                {showAddWorker && (
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
                    <h3 style={{ margin: '0 0 16px 0' }}>Yeni Worker Ekle</h3>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div className="form-group-inline">
                        <label>İsim</label>
                        <input type="text" placeholder="EU Worker 1" value={newWorker.name}
                          onChange={e => setNewWorker({ ...newWorker, name: e.target.value })} />
                      </div>
                      <div className="form-group-inline">
                        <label>IP Adresi</label>
                        <input type="text" placeholder="1.2.3.4" value={newWorker.ipAddress}
                          onChange={e => setNewWorker({ ...newWorker, ipAddress: e.target.value })} />
                      </div>
                      <div className="form-group-inline">
                        <label>Port</label>
                        <input type="number" value={newWorker.port}
                          onChange={e => setNewWorker({ ...newWorker, port: parseInt(e.target.value) || 4000 })}
                          style={{ width: '80px' }} />
                      </div>
                      <button className="btn-small btn-primary" onClick={handleAddWorker}>Ekle</button>
                      <button className="btn-small btn-secondary" onClick={() => setShowAddWorker(false)}>İptal</button>
                    </div>
                  </div>
                )}

                {/* Workers Table */}
                {workers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <p style={{ fontSize: '1.1rem' }}>Henüz worker sunucu eklenmemiş</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>Tüm screenshot'lar ana sunucuda alınıyor. Yük artınca yeni sunucu ekleyebilirsiniz.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>İsim</th>
                          <th>IP:Port</th>
                          <th>Durum</th>
                          <th>Sağlık</th>
                          <th>Screenshot</th>
                          <th>Başarısız</th>
                          <th>Ort. Süre</th>
                          <th>Son Kontrol</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workers.map(w => (
                          <tr key={w.id}>
                            <td><strong>{w.name}</strong></td>
                            <td style={{ fontFamily: 'monospace' }}>{w.ip_address}:{w.port}</td>
                            <td>
                              <span className={`badge ${w.is_active ? 'success' : 'secondary'}`}>
                                {w.is_active ? 'Aktif' : 'Pasif'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${w.is_healthy ? 'success' : 'danger'}`}>
                                {w.is_healthy ? '✅ Sağlıklı' : '❌ Yanıtsız'}
                              </span>
                              {w.last_health_error && (
                                <small style={{ display: 'block', color: '#ef4444', fontSize: '0.75rem' }}>{w.last_health_error}</small>
                              )}
                            </td>
                            <td>{w.total_screenshots || 0}</td>
                            <td>{w.failed_screenshots || 0}</td>
                            <td>{w.avg_response_time_ms ? `${w.avg_response_time_ms}ms` : '-'}</td>
                            <td style={{ fontSize: '0.85rem' }}>
                              {w.last_health_check ? formatDateForCountry(w.last_health_check, user?.country) : 'Hiç'}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                                <button className="btn-small btn-primary" onClick={() => handleWorkerHealthCheck(w.id)} title="Health Check">🔍</button>
                                <button className={`btn-small ${w.is_active ? 'btn-warning' : 'btn-success'}`}
                                  onClick={() => handleWorkerToggle(w.id, w.is_active)} title={w.is_active ? 'Pasife Al' : 'Aktif Et'}>
                                  {w.is_active ? '⏸' : '▶️'}
                                </button>
                                <button className="btn-small btn-secondary" onClick={() => handleRegenerateKey(w.id, w.name)} title="API Key Yenile">🔑</button>
                                <button className="btn-small btn-danger" onClick={() => handleWorkerDelete(w.id, w.name)} title="Sil">🗑</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Kurulum Bilgisi */}
                <div style={{ marginTop: '24px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#0369a1' }}>📖 Worker Kurulumu</h4>
                  <ol style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e', fontSize: '0.9rem', lineHeight: '1.8' }}>
                    <li>Yukarıdan "Yeni Worker Ekle" ile IP ve isim girin, oluşan API Key'i kopyalayın</li>
                    <li>Yeni sunucuya <code>worker-node/</code> klasörünü kopyalayın</li>
                    <li>Çalıştırın: <code>./setup.sh {'<ANA_SUNUCU_IP>'} {'<API_KEY>'}</code></li>
                    <li>Health Check butonuyla bağlantıyı doğrulayın</li>
                  </ol>
                </div>
              </section>
            )}

            {/* ═══════════════════ SİLİNEN HESAPLAR ═══════════════════ */}
            {activeTab === 'deleted-accounts' && (
              <section className="section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2>🗑️ Silinen Hesaplar ({deletedAccountsPagination.total})</h2>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Sayfa {deletedAccountsPagination.page} / {deletedAccountsPagination.totalPages}
                    {' '}· Toplam {deletedAccountsPagination.total} kayıt
                  </span>
                </div>

                {loading ? (
                  <p className="empty-message">Yükleniyor...</p>
                ) : deletedAccounts.length === 0 ? (
                  <p className="empty-message">Henüz silinmiş hesap kaydı yok.</p>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>E-posta</th>
                            <th>Ad Soyad</th>
                            <th>Silme Sebebi</th>
                            <th style={{ textAlign: 'center' }}>Domain</th>
                            <th style={{ textAlign: 'center' }}>Abonelik</th>
                            <th style={{ textAlign: 'center' }}>Check</th>
                            <th>IP Adresi</th>
                            <th>Silinme Tarihi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deletedAccounts.map(account => (
                            <tr key={account.id}>
                              <td>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {account.user_email}
                                </span>
                              </td>
                              <td>{account.full_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                              <td>
                                {account.deletion_reason && account.deletion_reason !== 'User requested account deletion' ? (
                                  <span
                                    title={account.deletion_reason}
                                    style={{
                                      display: 'inline-block',
                                      maxWidth: '220px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      verticalAlign: 'middle',
                                      color: '#374151'
                                    }}
                                  >
                                    {account.deletion_reason}
                                  </span>
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Belirtilmedi</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  minWidth: '28px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  background: account.domains_count > 0 ? '#dbeafe' : '#f3f4f6',
                                  color: account.domains_count > 0 ? '#1d4ed8' : '#9ca3af',
                                  fontWeight: 600,
                                  fontSize: '0.8rem'
                                }}>
                                  {account.domains_count}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  minWidth: '28px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  background: account.subscriptions_count > 0 ? '#dcfce7' : '#f3f4f6',
                                  color: account.subscriptions_count > 0 ? '#15803d' : '#9ca3af',
                                  fontWeight: 600,
                                  fontSize: '0.8rem'
                                }}>
                                  {account.subscriptions_count}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                  {account.checks_count?.toLocaleString('tr-TR') ?? 0}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>
                                  {account.ip_address || '—'}
                                </span>
                              </td>
                              <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                {formatDateForCountry(account.deleted_at, user?.country)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {deletedAccountsPagination.totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '24px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          className="btn-small"
                          disabled={!deletedAccountsPagination.hasPrev}
                          onClick={() => handleDeletedAccountsPageChange(1)}
                          style={{ opacity: deletedAccountsPagination.hasPrev ? 1 : 0.4 }}
                        >
                          ««
                        </button>
                        <button
                          className="btn-small"
                          disabled={!deletedAccountsPagination.hasPrev}
                          onClick={() => handleDeletedAccountsPageChange(deletedAccountsPagination.page - 1)}
                          style={{ opacity: deletedAccountsPagination.hasPrev ? 1 : 0.4 }}
                        >
                          ‹ Önceki
                        </button>

                        {/* Sayfa numaraları */}
                        {Array.from({ length: Math.min(7, deletedAccountsPagination.totalPages) }, (_, i) => {
                          const total = deletedAccountsPagination.totalPages;
                          const current = deletedAccountsPagination.page;
                          let pageNum;
                          if (total <= 7) {
                            pageNum = i + 1;
                          } else if (current <= 4) {
                            pageNum = i + 1;
                            if (i === 6) pageNum = total;
                            if (i === 5) pageNum = null; // ellipsis
                          } else if (current >= total - 3) {
                            pageNum = i === 0 ? 1 : i === 1 ? null : total - (6 - i);
                          } else {
                            const map = [1, null, current - 1, current, current + 1, null, total];
                            pageNum = map[i];
                          }

                          if (pageNum === null) {
                            return <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#9ca3af' }}>…</span>;
                          }
                          return (
                            <button
                              key={pageNum}
                              className="btn-small"
                              onClick={() => handleDeletedAccountsPageChange(pageNum)}
                              style={{
                                background: pageNum === current ? '#2563eb' : undefined,
                                color: pageNum === current ? '#fff' : undefined,
                                fontWeight: pageNum === current ? 700 : undefined,
                                minWidth: '36px'
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          className="btn-small"
                          disabled={!deletedAccountsPagination.hasNext}
                          onClick={() => handleDeletedAccountsPageChange(deletedAccountsPagination.page + 1)}
                          style={{ opacity: deletedAccountsPagination.hasNext ? 1 : 0.4 }}
                        >
                          Sonraki ›
                        </button>
                        <button
                          className="btn-small"
                          disabled={!deletedAccountsPagination.hasNext}
                          onClick={() => handleDeletedAccountsPageChange(deletedAccountsPagination.totalPages)}
                          style={{ opacity: deletedAccountsPagination.hasNext ? 1 : 0.4 }}
                        >
                          »»
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {/* ── HEDİYE GÜN SEKMESİ ── */}
            {activeTab === 'gift' && (
              <section className="section">
                <h2>🎁 Hediye Gün Ver</h2>
                <p style={{ color: '#6b7280', marginBottom: 24 }}>
                  Kullanıcıların aktif domain aboneliklerine ücretsiz süre ekle.
                  Email alanına <strong>all</strong> yazarsan tüm aktif aboneliklere eklenir.
                </p>
                <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.875rem' }}>
                      Email <span style={{ color: '#6b7280', fontWeight: 400 }}>(veya "all")</span>
                    </label>
                    <input
                      type="text"
                      value={giftEmail}
                      onChange={e => { setGiftEmail(e.target.value); setGiftResult(null); }}
                      placeholder="user@example.com veya all"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.875rem' }}>
                      Domain <span style={{ color: '#6b7280', fontWeight: 400 }}>(opsiyonel — boş bırakırsan tüm domainleri)</span>
                    </label>
                    <input
                      type="text"
                      value={giftDomain}
                      onChange={e => { setGiftDomain(e.target.value); setGiftResult(null); }}
                      placeholder="https://example.com (opsiyonel)"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.875rem' }}>
                      Eklenecek Gün Sayısı
                    </label>
                    <input
                      type="number"
                      value={giftDays}
                      onChange={e => { setGiftDays(e.target.value); setGiftResult(null); }}
                      placeholder="30"
                      min="1"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button
                    onClick={handleGift}
                    disabled={giftLoading || !giftEmail.trim() || !giftDays || parseInt(giftDays) <= 0}
                    style={{
                      padding: '11px 24px', background: '#059669', color: '#fff', border: 'none',
                      borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                      opacity: (giftLoading || !giftEmail.trim() || !giftDays || parseInt(giftDays) <= 0) ? 0.5 : 1
                    }}
                  >
                    {giftLoading ? '⏳ Uygulanıyor...' : '🎁 Hediye Gönder'}
                  </button>
                  {giftResult && (
                    <div style={{
                      padding: '12px 16px', borderRadius: 8,
                      background: giftResult.success ? '#d1fae5' : '#fee2e2',
                      color: giftResult.success ? '#065f46' : '#991b1b',
                      fontWeight: 500
                    }}>
                      {giftResult.success
                        ? `✅ ${giftResult.affected} domain'e ${giftResult.days} gün eklendi.`
                        : `❌ Hata: ${giftResult.error === 'USER_NOT_FOUND' ? 'Kullanıcı bulunamadı.' : giftResult.error === 'INVALID_DAYS' ? 'Geçersiz gün değeri.' : 'Bir hata oluştu.'}`
                      }
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ── MAİL GÖNDER SEKMESİ ── */}
            {activeTab === 'email-broadcast' && (
              <section className="section">
                <h2>📧 Mail Gönder</h2>
                <p style={{ color: '#6b7280', marginBottom: 24 }}>
                  Tek bir kullanıcıya veya tüm kullanıcılara (kayıtlı dillerine göre) e-posta gönder.
                  Email alanına <strong>all</strong> yazarsan tüm aktif kullanıcılara gönderilir.
                </p>

                <div style={{ maxWidth: 700, display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Alıcı */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.875rem' }}>
                      Alıcı Email <span style={{ color: '#6b7280', fontWeight: 400 }}>(veya "all")</span>
                    </label>
                    <input
                      type="text"
                      value={broadcastEmail}
                      onChange={e => { setBroadcastEmail(e.target.value); setBroadcastResult(null); }}
                      placeholder="user@example.com veya all"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* İçerikler yan yana */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

                    {/* Türkçe */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', background: '#fef3c7', borderRadius: 8,
                        fontWeight: 600, fontSize: '0.875rem', color: '#92400e'
                      }}>
                        🇹🇷 Türkçe İçerik
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.8rem', color: '#374151' }}>
                          Konu
                        </label>
                        <input
                          type="text"
                          value={broadcastSubjectTr}
                          onChange={e => { setBroadcastSubjectTr(e.target.value); setBroadcastResult(null); }}
                          placeholder="E-posta konusu (Türkçe)"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.8rem', color: '#374151' }}>
                          İçerik
                        </label>
                        <textarea
                          value={broadcastContentTr}
                          onChange={e => { setBroadcastContentTr(e.target.value); setBroadcastResult(null); }}
                          placeholder="E-posta içeriği (Türkçe)&#10;HTML desteklenir."
                          rows={10}
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    </div>

                    {/* İngilizce */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', background: '#dbeafe', borderRadius: 8,
                        fontWeight: 600, fontSize: '0.875rem', color: '#1e40af'
                      }}>
                        🇬🇧 İngilizce İçerik
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.8rem', color: '#374151' }}>
                          Subject
                        </label>
                        <input
                          type="text"
                          value={broadcastSubjectEn}
                          onChange={e => { setBroadcastSubjectEn(e.target.value); setBroadcastResult(null); }}
                          placeholder="Email subject (English)"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.8rem', color: '#374151' }}>
                          Content
                        </label>
                        <textarea
                          value={broadcastContentEn}
                          onChange={e => { setBroadcastContentEn(e.target.value); setBroadcastResult(null); }}
                          placeholder="Email content (English)&#10;HTML is supported."
                          rows={10}
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Uyarı */}
                  <div style={{
                    padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, fontSize: '0.8rem', color: '#78350f'
                  }}>
                    ⚠️ <strong>Not:</strong> Kullanıcının dili <code>tr</code> ise Türkçe, diğer tüm diller için İngilizce içerik gönderilir.
                    Email bildirimleri kapalı olan kullanıcılar otomatik olarak atlanır.
                    {broadcastEmail.trim().toLowerCase() === 'all' && (
                      <span style={{ display: 'block', marginTop: 4, color: '#991b1b', fontWeight: 600 }}>
                        ❗ "all" seçildi — tüm aktif kullanıcılara gönderilecek. Gönder butonuna basmadan önce içerikleri kontrol edin.
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleBroadcast}
                    disabled={
                      broadcastLoading ||
                      !broadcastEmail.trim() ||
                      (!broadcastSubjectTr.trim() && !broadcastSubjectEn.trim()) ||
                      (!broadcastContentTr.trim() && !broadcastContentEn.trim())
                    }
                    style={{
                      padding: '11px 28px', background: '#2563eb', color: '#fff', border: 'none',
                      borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                      alignSelf: 'flex-start',
                      opacity: (broadcastLoading || !broadcastEmail.trim() || (!broadcastSubjectTr.trim() && !broadcastSubjectEn.trim()) || (!broadcastContentTr.trim() && !broadcastContentEn.trim())) ? 0.5 : 1
                    }}
                  >
                    {broadcastLoading ? '⏳ Gönderiliyor...' : '📧 Mail Gönder'}
                  </button>

                  {broadcastResult && (
                    <div style={{
                      padding: '12px 16px', borderRadius: 8,
                      background: broadcastResult.success ? '#d1fae5' : '#fee2e2',
                      color: broadcastResult.success ? '#065f46' : '#991b1b',
                      fontWeight: 500
                    }}>
                      {broadcastResult.success
                        ? `✅ ${broadcastResult.sent} kullanıcıya mail gönderildi.${broadcastResult.skipped > 0 ? ` (${broadcastResult.skipped} kullanıcı atlandı — bildirim kapalı veya bulunamadı)` : ''}`
                        : `❌ Hata: ${
                            broadcastResult.error === 'USER_NOT_FOUND' ? 'Kullanıcı bulunamadı.' :
                            broadcastResult.error === 'MISSING_CONTENT' ? 'En az bir dil için konu ve içerik gerekli.' :
                            broadcastResult.error === 'MISSING_EMAIL' ? 'Email adresi gerekli.' :
                            'Bir hata oluştu.'
                          }`
                      }
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Admin-specific Styles */}
      <style>{`
        .admin-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .admin-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 950px;
          max-height: 85vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        .admin-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          background: white;
          border-radius: 12px 12px 0 0;
          z-index: 1;
        }
        .admin-modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .btn-close {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          color: #6b7280;
        }
        .btn-close:hover {
          background: #f3f4f6;
          color: #111827;
        }
        .admin-modal-body {
          padding: 24px;
        }
        .user-detail-info {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .detail-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .detail-label {
          font-weight: 600;
          color: #374151;
          min-width: 120px;
        }
        .clickable-row:hover {
          background-color: #f3f4f6;
          transition: background-color 0.15s;
        }
        .plan-edit-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }
        .plan-edit-form {
          display: flex;
          gap: 16px;
          align-items: flex-end;
          flex-wrap: wrap;
        }
        .form-group-inline {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .form-group-inline label {
          font-size: 0.8rem;
          font-weight: 600;
          color: #374151;
        }
        .form-group-inline input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
        }
        .inline-edit {
          padding: 4px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.9rem;
          width: 100%;
        }
        .btn-primary {
          background-color: #3b82f6;
          color: white;
          border: none;
        }
        .btn-primary:hover {
          background-color: #2563eb;
        }
        .btn-secondary {
          background-color: #6b7280;
          color: white;
          border: none;
        }
        .btn-secondary:hover {
          background-color: #4b5563;
        }
        .btn-warning {
          background-color: #f59e0b;
          color: white;
          border: none;
        }
        .btn-warning:hover {
          background-color: #d97706;
        }
        .badge.danger {
          background-color: #fef2f2;
          color: #ef4444;
        }
        .pricing-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
        }
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 20px;
        }
        .pricing-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .pricing-field label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #374151;
        }
        .pricing-field input {
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
        }
        .pricing-input-group {
          display: flex;
          align-items: center;
          gap: 0;
        }
        .pricing-prefix {
          padding: 10px 12px;
          background: #e5e7eb;
          border: 1px solid #d1d5db;
          border-right: none;
          border-radius: 8px 0 0 8px;
          font-weight: 600;
          color: #374151;
        }
        .pricing-input-group input {
          border-radius: 0 8px 8px 0 !important;
        }
        .pricing-display {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .pricing-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .pricing-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
        }
        .pricing-highlight {
          color: #059669;
        }
        .pricing-preview {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }
        .pricing-preview h4 {
          margin: 0 0 4px 0;
          font-size: 0.95rem;
          color: #374151;
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;
